<template>
  <div class="search-modal-trigger" :class="{ 'search-modal-active': isSearchModalActive }">
    <div ref="triggerEl" class="search-modal-transform" :class="{ 'mew-search-modal-transform-clean': experiments.mewWhitespaceRemovalEnabled }">
      <slot :trigger-id="triggerId" :open-overlay="openOverlay"></slot>
      <div :id="`search-teleport-${triggerId}`" class="search-modal-teleport-target"></div>
    </div>
  </div>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { ref, computed, useId, onMounted, nextTick } from 'vue';
import { SET_ACTIVE_SEARCH_TRIGGER_ID, SET_SEARCH_MODAL_STATE } from '#shared/types/actions';
import { useMobileNav } from '~/composables/useMobileNav';
import { tagSearchBarClick } from '#shared/features/analytics/searchTags';
import useExperiments from '~/composables/useExperiments';

const props = defineProps({
  isMobile: {
    type: Boolean,
    default: false,
  },
});

const triggerEl = ref(null);
const triggerId = useId();
const navHeader = useNavHeader();
const experiments = useExperiments();
const { isMenuOpened, closeNav } = useMobileNav();
const isSearchModalActive = computed(() => navHeader.isSearchModalActive);

function openOverlay() {
  if (isMenuOpened.value) closeNav();
  navHeader[SET_ACTIVE_SEARCH_TRIGGER_ID](triggerId);
  navHeader[SET_SEARCH_MODAL_STATE](true);

  // Analytics
  tagSearchBarClick(props.isMobile);
}

onMounted(() => {
  if (navHeader.activeSearchTriggerId === null) {
    nextTick(() => {
      const input = triggerEl.value?.querySelector('.input-search');

      openOverlay();
      input?.focus();
    });
  }
});

defineExpose({
  openOverlay,
});
</script>

<style lang="scss">
.search-modal-trigger {
  position: relative;
  width: 100%;

  .search-modal-transform {
    position: relative;
    top: 0;
    transition: none;
  }
}

.search-modal-active {
  .search-modal-transform {
    position: absolute;
    top: 0;
    z-index: 30; // Ensures it overlays everything
  }
}
</style>

<style lang="scss" brand="mcom">
$search-desktop-width: 706px;
$search-small-desktop-width: 429px;

.search-modal-trigger {
  .search-modal-transform {
    width: 100%;
    max-width: $search-desktop-width;

    @include breakpoint(medium down) {
      max-width: 100%;
    }
  }

  &.search-modal-active {
    .mew-search-modal-transform-clean {
      transition: transform 260ms ease-out;
    }

    .search-modal-transform {
      @include breakpoint(medium down) {
        max-width: 100%;
        top: -15px;

        &.mew-search-modal-transform-clean {
          top: 0;
          transform: translateY(-31px);
        }
      }
      @include breakpoint(large up) {
        min-width: $search-small-desktop-width;
      }
    }
  }
}

.search-condensed-open {
  .search-modal-trigger {
    position: absolute;
    left: 16px;
    right: 16px;
    width: auto;

    .search-modal-transform {
      top: 10px;
    }

    &.search-modal-active {
      .mew-search-modal-transform-clean {
        transform: translateY(-6px);
      }
    }
  }
}
</style>

<style lang="scss" brand="bcom">
.search-modal-active {
  .search-modal-transform {
    width: 100%;

    @include breakpoint(medium down) {
      top: -28px;

      &.mew-search-modal-transform-clean {
        top: 0;
        transform: translateY(-36px);
        transition: transform 360ms ease-out;
      }
    }

    @include breakpoint(large only) {
      min-width: 440px;
      margin-left: -44px;
    }

    @include breakpoint(mlarge up) {
      width: 732px;
      margin-left: -142px;
    }
  }
}
</style>
